/*
  Keeps the main product form's quantity box and Vitals' volume-discount tiers
  in step, both ways (cards and radio layouts). Loaded by main-product with
  vitals-gift-match.js.

  - Picking a tier: Vitals itself sets the box to the tier's quantity.
  - Changing the box: the tier with the highest "Buy N" at or below the box is
    picked (6 with tiers 2/3/4 → "Buy 4 or MORE"), or the tier is cleared when
    the box is below every tier. The box keeps the customer's number; above the
    top tier, assets/vitals-cart-add.js adds the box's quantity. Vitals sets
    the box to the tier's quantity after a pick, sometimes several hundred ms
    later (seen in Safari), so for a short while the customer's number is put
    back whenever it changes — until the customer taps or types again.

  Tier quantities come from the "Buy N" in each tier's label; a widget with a
  tier that has no number is left alone.

  Also exposes window.vitalsTiers.selected(productInfo) for the sticky ATC.
*/
(function () {
  const WIDGET = '.vtl-ub-vd-widget';
  const TIER = '[class*="__TierItem"]';
  const isTier = (element) => /__TierItem(\s|$)/.test(element.className);
  const isSelected = (tier) => /__TierItem--Selected/.test(tier.className);

  const tiersOf = (widget) =>
    [...widget.querySelectorAll(TIER)].filter(isTier).map((element) => {
      const match = element.textContent.match(/buy\s*(\d+)/i);
      return { element, quantity: match ? parseInt(match[1], 10) : null };
    });

  const widgetOf = (productInfo) => productInfo?.querySelector(WIDGET) || null;

  const selected = (productInfo) => {
    const widget = widgetOf(productInfo);
    return widget ? tiersOf(widget).find((tier) => isSelected(tier.element)) || null : null;
  };

  window.vitalsTiers = { selected };

  // A tier is picked by clicking its label, as a customer would (cards:
  // __Label; radio: __TierLabel).
  const clickTier = (tier) =>
    (
      tier.element.querySelector('[class*="__Label"]') ||
      tier.element.querySelector('[class*="__TierLabel"]') ||
      tier.element
    ).click();

  const KEEP_MS = 1500;

  // Puts the customer's number back in the box whenever Vitals changes it,
  // for KEEP_MS or until the customer uses the box again.
  const keepQuantity = (input, quantity, done) => {
    const control = input.closest('quantity-input') || input;
    let customer = false;
    const onCustomer = () => (customer = true);
    control.addEventListener('pointerdown', onCustomer, true);
    control.addEventListener('keydown', onCustomer, true);
    const until = performance.now() + KEEP_MS;
    const tick = () => {
      if (!customer && input.value !== String(quantity)) {
        input.value = quantity;
        input.closest('quantity-input')?.validateQtyRules?.();
      }
      if (!customer && performance.now() < until) return requestAnimationFrame(tick);
      control.removeEventListener('pointerdown', onCustomer, true);
      control.removeEventListener('keydown', onCustomer, true);
      done();
    };
    requestAnimationFrame(tick);
  };

  let syncing = false;
  let pending = null; // a box changed by the customer while syncing

  const onQuantityChange = (event) => {
    if (!event.target.matches('input[name="quantity"]')) return;
    if (syncing) {
      pending = event.target;
      return;
    }
    const productInfo = event.target.closest('product-info');
    const widget = widgetOf(productInfo);
    if (!widget) return;

    const tiers = tiersOf(widget);
    if (!tiers.length || tiers.some((tier) => !tier.quantity)) return;

    const input = event.target;
    const quantity = parseInt(input.value, 10);
    if (!Number.isInteger(quantity) || quantity < 1) return;

    const target = tiers
      .filter((tier) => tier.quantity <= quantity)
      .reduce((best, tier) => (!best || tier.quantity > best.quantity ? tier : best), null);
    const current = tiers.find((tier) => isSelected(tier.element)) || null;
    if (target === current || (!target && !current)) return;

    syncing = true;
    pending = null;
    clickTier(target || current); // clicking the selected tier clears it
    keepQuantity(input, quantity, () => {
      syncing = false;
      if (pending) onQuantityChange({ target: pending });
    });
  };

  document.addEventListener('change', onQuantityChange);
})();
